import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { themeProp, useIsMounted } from '@vmos2/utils';
import get from 'lodash/get';
import { transparentize } from 'polished';
import React, { useLayoutEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { Icon } from '../icons';
import { useComponentTheme } from '../useComponentTheme';

type Props = {
  children: ReactNode,
  className?: string,
  isVertical?: boolean,
  mode?: 'default' | 'vertical',
};

export const MenuNavigation = ({ isVertical, mode, children, className }: Props) => {
  const { t } = useTranslation('accessibility');
  const componentTheme = useComponentTheme(theme => theme.components.Navbar, mode);
  const isMounted = useIsMounted();
  const listRef = useRef(null);
  const [scrollState, setScrollState] = useState();

  useLayoutEffect(() => {
    const listElement = listRef.current;

    const computeChevrons = () => {
      if (listElement) {
        const { scrollWidth, scrollHeight, scrollLeft, scrollTop, clientWidth, clientHeight } = listElement;

        const result = {
          scrollableLeft: scrollLeft > 1,
          scrollableRight: Math.floor(scrollWidth - scrollLeft) > clientWidth,
          scrollableTop: scrollTop > 1,
          scrollableBottom: Math.floor(scrollHeight - scrollTop) > clientHeight,
        };

        if (isMounted.current) {
          setScrollState(result);
        }
      }
    };

    // The width of the navigation on the kiosk is `0` on page load, so add a timeout to give it some time to render
    // properly.
    window.setTimeout(computeChevrons, 500);

    listElement.addEventListener('scroll', computeChevrons, { passive: true });

    return () => {
      listElement.removeEventListener('scroll', computeChevrons, { passive: true });
    };
  }, [isMounted]);

  return (
    <Container componentTheme={componentTheme} data-test="menu-navigation" isVertical={isVertical}>
      <h2 className="hide-text">{t('menuNavigation')}</h2>
      <List
        className={className}
        componentTheme={componentTheme}
        data-test="menu-navigation-list"
        isVertical={isVertical}
        ref={listRef}
      >
        {children}
      </List>

      {isVertical ? (
        <>
          {get(scrollState, 'scrollableTop') && (
            <ChevronTop data-test="menu-arrow-top" componentTheme={componentTheme}>
              <Icon name="ChevronRight" />
            </ChevronTop>
          )}

          {get(scrollState, 'scrollableBottom') && (
            <ChevronBottom data-test="menu-arrow-bottom" componentTheme={componentTheme}>
              <Icon name="ChevronRight" />
            </ChevronBottom>
          )}
        </>
      ) : (
        <>
          {get(scrollState, 'scrollableLeft') && (
             <ChevronLeft data-test="menu-arrow-left" componentTheme={componentTheme}>
              <Icon name="ChevronRight" />
            </ChevronLeft>
          )}

          {get(scrollState, 'scrollableRight') && (
            <ChevronRight data-test="menu-arrow-right" componentTheme={componentTheme}>
              <Icon name="ChevronRight" />
            </ChevronRight>
          )}
        </>
      )}
    </Container>
  );
};

const Container = styled.nav`
  position: relative;
  width: 100%;
  height: 100%;
  padding: ${({ isVertical }) => !isVertical && '0 24px'};
  background-color: ${({ componentTheme }) => componentTheme.Container.backgroundColor};
  box-shadow: ${({ componentTheme }) => componentTheme.Container.boxShadow};

  @media (${themeProp('breakpoint.tabletLarge')}) {
    padding: ${({ isVertical }) => !isVertical && '0 32px'};
  }
`;

const List = styled.ul`
  position: relative;
  display: inline-flex;
  flex-direction: ${({ isVertical }) => isVertical && 'column'};
  list-style: none;
  padding: ${({ componentTheme }) => componentTheme.Container.padding || 0};
  margin: 0;
  width: 100%;
  height: 100%;
  overflow: auto;
  ${({ componentTheme }) => componentTheme.Container.gap && `gap: ${componentTheme.Container.gap};`};

  @media (${themeProp('breakpoint.mobile')}) {
    scrollbar-width: none;

    &::-webkit-scrollbar {
      display: none;
    }
  }
`;

const chevronStyles = ({ theme, componentTheme }) => css`
  position: absolute;
  pointer-events: none;
  display: flex;
  align-items: center;
  font-size: ${theme.fontSize.xs};
  color: ${theme.color.brand};
  padding: 0 16px;
  justify-content: flex-end;
  background: linear-gradient(
    90deg,
    ${transparentize(1, componentTheme.Container.backgroundColor)} 0%,
    ${componentTheme.Container.backgroundColor} 100%
  );
`;

const chevronXStyles = ({ theme }) => css`
  top: 0;
  width: 60px;
  height: 100%;

  @media (${theme.breakpoint.tabletLarge}) {
    font-size: ${theme.fontSize.sm};
    width: 120px;
  }
`;

const ChevronRight = styled.span`
  ${chevronStyles};
  ${chevronXStyles};
  right: 0;
`;

const ChevronLeft = styled.span`
  ${chevronStyles};
  ${chevronXStyles};
  left: 0;
  transform: rotate(180deg);
`;

const ChevronTop = styled.span`
  ${chevronStyles};
  top: 0;
  height: 180px;
  width: 100%;
  transform: rotate(270deg);
`;

const ChevronBottom = styled.span`
  ${chevronStyles};
  bottom: 0;
  height: 180px;
  width: 100%;
  transform: rotate(90deg);
`;
